/**
 * Utility for generating and verifying secure access tokens for coupons
 * These tokens prove ownership and allow authentication without fetching the coupon first
 */

import CryptoJS from 'crypto-js';

const TOKEN_SECRET = import.meta.env.VITE_COUPON_TOKEN_SECRET;

/**
 * Generate a secure hash using Web Crypto API, with crypto-js fallback for HTTP/local network
 * Both methods produce SHA-256 hashes compatible with server-side verification
 */
async function generateHash(data: string): Promise<string> {
    // Try Web Crypto API first (requires HTTPS or localhost)
    if (crypto && crypto.subtle) {
        try {
            const encoder = new TextEncoder();
            const dataBuffer = encoder.encode(data);
            const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
            return parseInt(hashHex.substring(0, 8), 16).toString(36);
        } catch (error) {
            console.warn('crypto.subtle failed, using crypto-js fallback:', error);
        }
    }

    // Fallback: Use crypto-js for HTTP/local network access (works without secure context)
    // This produces the same SHA-256 hash format as crypto.subtle
    const hashHex = CryptoJS.SHA256(data).toString(CryptoJS.enc.Hex);
    return parseInt(hashHex.substring(0, 8), 16).toString(36);
}

/**
 * Generate a secure access token for a coupon
 * This token proves ownership and can be included in the reward URL
 * Format: base64(playerId:couponId:clientID:timestamp:hash)
 */
export async function generateCouponToken(playerId: string, couponId: string, clientID: string): Promise<string> {
    if (!TOKEN_SECRET) {
        throw new Error('TOKEN_SECRET is not set');
    }
    const timestamp = Date.now();
    const data = `${playerId}:${couponId}:${clientID}:${timestamp}`;
    const combined = data + TOKEN_SECRET;
    const hash = await generateHash(combined);
    const token = btoa(`${data}:${hash}`);
    return token;
}

/**
 * Verify and extract data from a coupon access token
 * Returns the playerId if token is valid, null otherwise
 */
export async function verifyCouponToken(token: string, couponId: string, clientID: string): Promise<string | null> {
    try {
        const decoded = atob(token);
        const parts = decoded.split(':');

        if (parts.length !== 5) {
            return null;
        }

        const [playerId, tokenCouponId, tokenClientID, timestamp, hash] = parts;

        // Verify couponId and clientID match
        if (tokenCouponId !== couponId || tokenClientID !== clientID) {
            return null;
        }

        // Verify token is not too old (optional - tokens valid for 30 days)
        const tokenAge = Date.now() - parseInt(timestamp, 10);
        const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days
        if (tokenAge > maxAge) {
            return null;
        }

        // Verify hash using crypto
        const data = `${playerId}:${tokenCouponId}:${tokenClientID}:${timestamp}`;
        const combined = data + TOKEN_SECRET;
        const expectedHash = await generateHash(combined);

        if (expectedHash !== hash) {
            return null;
        }

        return playerId;
    } catch (error) {
        return null;
    }
}
